import React, { useCallback, useRef, useState } from 'react';
import { AbsoluteTimeRange, DataFrame, GrafanaTheme2, LoadingState, TimeRange, dateTime } from '@grafana/data';
import {
  SceneObjectState,
  SceneObjectBase,
  SceneComponentProps,
  SceneQueryRunner,
  SceneTimeRange,
  sceneGraph,
} from '@grafana/scenes';
import { Text, useStyles2, Spinner } from '@grafana/ui';
import { css } from '@emotion/css';

import { TimeSeeker } from './TimeSeeker';
import { getTraceExplorationScene } from 'utils/utils';
import { explorationDS, MetricFunction, DEFAULT_QUERY_RANGE_HOURS } from 'utils/shared';
import { getMetricsTempoQuery } from '../queries/generateMetricsQuery';
import { BatchDataCache } from './BatchCache';

const DEFAULT_WINDOW_MS = 12 * 60 * 60 * 1000; // 12 hours for visible range

export interface TimeSeekerSceneState extends SceneObjectState {
  queryRangeHours?: number;
}

export class TimeSeekerScene extends SceneObjectBase<TimeSeekerSceneState> {
  private batchCache: BatchDataCache;
  private currentMetric: MetricFunction | null = null;
  private visibleRange: AbsoluteTimeRange | null = null;
  private currentBatchSubscription: (() => void) | null = null;

  constructor(state: Partial<TimeSeekerSceneState> = {}) {
    super({
      queryRangeHours: DEFAULT_QUERY_RANGE_HOURS,
      ...state,
    });
    this.batchCache = new BatchDataCache(this.state.queryRangeHours!);
    this.addActivationHandler(this._onActivate.bind(this));
  }

  private _onActivate() {
    const traceExploration = getTraceExplorationScene(this);
    const metricVar = traceExploration.getMetricVariable();

    // Subscribe to metric changes - need to clear cache
    this._subs.add(
      metricVar.subscribeToState((newState) => {
        const newMetric = newState.value as MetricFunction;
        if (this.currentMetric !== newMetric) {
          this.currentMetric = newMetric;
          this.batchCache.clearCache();
          this.loadNextBatch();
        }
      })
    );

    this.currentMetric = metricVar.state.value as MetricFunction;

    // Cleanup on deactivation
    return () => {
      this.batchCache.clearCache();
      this.cleanupBatchSubscription();
    };
  }

  /**
   * Clean up the current batch subscription if it exists.
   */
  private cleanupBatchSubscription(): void {
    if (this.currentBatchSubscription) {
      this.currentBatchSubscription();
      this.currentBatchSubscription = null;
    }
  }

  /**
   * Update the visible range and trigger batch loading if needed.
   */
  public updateVisibleRange(visibleFrom: number, visibleTo: number): void {
    this.visibleRange = { from: visibleFrom, to: visibleTo };

    if (!this.currentMetric) {
      return;
    }

    // Load next batch if needed
    // Note: Metric changes are handled by the subscription in _onActivate(),
    // no need to check on every visible range update
    this.loadNextBatch();
  }

  /**
   * Load the next batch that's needed for the visible range.
   */
  private loadNextBatch(): void {
    if (!this.visibleRange || !this.currentMetric) {
      return;
    }

    // Check if there's already a batch loading
    if (this.batchCache.getLoadingBatchId() !== null) {
      return;
    }

    const nextBatch = this.batchCache.getNextBatchToLoad(this.visibleRange.from, this.visibleRange.to);
    if (!nextBatch) {
      return;
    }

    // Mark batch as loading
    this.batchCache.setLoadingBatch(nextBatch.batchId);

    // Create query
    const query = getMetricsTempoQuery({ metric: this.currentMetric, sample: true });

    // Create time range for this batch
    const $timeRange = new SceneTimeRange({
      from: dateTime(nextBatch.from).toISOString(),
      to: dateTime(nextBatch.to).toISOString(),
    });

    // Create a transformer that caches the result and merges with existing data
    const batchId = nextBatch.batchId;
    const batchFrom = nextBatch.from;
    const batchTo = nextBatch.to;
    const cache = this.batchCache;
    const loadNextBatchFn = this.loadNextBatch.bind(this);
    const forceRenderFn = () => this.forceRender();

    // Clean up previous batch subscription
    this.cleanupBatchSubscription();

    // Update the $data with new query runner
    this.setState({
      $data: new SceneQueryRunner({
        datasource: explorationDS,
        queries: [{ ...query, step: '30s' }],
        $timeRange,
      }),
    });

    // Subscribe to data updates
    const data = sceneGraph.getData(this);
    /** True after terminal outcome (Done or Error) for this query; streaming can emit many times before that. */
    let batchCompleted = false;

    const processQueryState = (state: { data?: { state: LoadingState; series: DataFrame[]; errors?: Array<{ message?: string }> } }) => {
      if (batchCompleted) {
        return;
      }

      if (cache.getLoadingBatchId() !== batchId) {
        return;
      }

      const panelData = state.data;
      if (!panelData) {
        return;
      }

      if (panelData.state === LoadingState.Error) {
        batchCompleted = true;
        const errorMessage = panelData.errors?.[0]?.message || 'Failed to load data';
        cache.storeBatchError(batchId, batchFrom, batchTo, errorMessage);
        forceRenderFn();
        setTimeout(() => loadNextBatchFn(), 0);
        return;
      }

      // Streaming: refresh the cached snapshot when frames arrive, but keep this batch "loading"
      // until Done so we do not start the next batch early and so we do not miss later packets.
      if (panelData.state === LoadingState.Streaming) {
        if ((panelData.series?.length ?? 0) > 0) {
          cache.storeBatch(batchId, batchFrom, batchTo, panelData.series, undefined, false);
          forceRenderFn();
        }
        return;
      }

      if (panelData.state === LoadingState.Done) {
        batchCompleted = true;
        cache.storeBatch(batchId, batchFrom, batchTo, panelData.series);
        forceRenderFn();
        setTimeout(() => loadNextBatchFn(), 0);
      }
    };

    const subscription = data.subscribeToState((state) => {
      processQueryState(state);
    });

    // subscribeToState does not emit the current value; process it so we never miss a sync or very fast Done.
    processQueryState(data.state);

    // Store subscription for cleanup
    this.currentBatchSubscription = () => subscription.unsubscribe();
    this._subs.add(subscription);
  }

  /**
   * Get concatenated data for the visible range from cache.
   */
  public getCachedData(): { series: DataFrame[]; loading: boolean } {
    if (!this.visibleRange) {
      return { series: [], loading: false };
    }

    const series = this.batchCache.getCachedData(this.visibleRange.from, this.visibleRange.to);
    const loading = !this.batchCache.isFullyLoaded(this.visibleRange.from, this.visibleRange.to);

    return { series, loading };
  }

  /**
   * Get loading ranges for UI display.
   */
  public getLoadingRanges(): Array<{ from: number; to: number }> {
    if (!this.visibleRange) {
      return [];
    }
    return this.batchCache.getLoadingRanges(this.visibleRange.from, this.visibleRange.to);
  }

  /**
   * Check if the visible range requires a large number of batches (performance warning).
   */
  public hasLargeBatchWarning(): boolean {
    return this.batchCache.hasLargeBatchWarning();
  }

  /**
   * Get errors from the visible range.
   */
  public getErrors(): string[] {
    if (!this.visibleRange) {
      return [];
    }
    return this.batchCache.getErrors(this.visibleRange.from, this.visibleRange.to);
  }

  /**
   * Clear errors and retry loading failed batches.
   */
  public retryErrors(): void {
    this.batchCache.clearErrors();
    this.loadNextBatch();
    this.forceRender();
  }

  private buildContextRange(selectionTimeRange?: TimeRange): AbsoluteTimeRange {
    if (!selectionTimeRange) {
      const now = Date.now();
      return {
        from: now - DEFAULT_WINDOW_MS,
        to: now,
      };
    }

    const selectionFrom = selectionTimeRange.from.valueOf();
    const selectionTo = selectionTimeRange.to.valueOf();
    const selectionMid = (selectionFrom + selectionTo) / 2;
    const halfWindow = DEFAULT_WINDOW_MS / 2;
    let from = selectionMid - halfWindow;
    let to = selectionMid + halfWindow;
    const now = Date.now();

    if (to > now) {
      const shift = to - now;
      to = now;
      from -= shift;
    }

    return { from, to };
  }

  public static Component = ({ model }: SceneComponentProps<TimeSeekerScene>) => {
    const styles = useStyles2(getStyles);
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    const [, forceUpdate] = useState(0);

    // Store forceRender callback on the model
    React.useEffect(() => {
      model.forceRender = () => forceUpdate((n) => n + 1);
    }, [model]);

    const traceExploration = getTraceExplorationScene(model);
    const metricVar = traceExploration.getMetricVariable();
    const { value: metricValue } = metricVar.useState();
    const sceneTimeRange = traceExploration.state.$timeRange;
    const sceneTimeRangeState = sceneTimeRange?.useState();
    const selectionTimeRange = sceneTimeRangeState?.value;

    // Track visible range for batch loading
    const [visibleRange, setVisibleRange] = useState<AbsoluteTimeRange>(() => {
      const contextRange = model.buildContextRange(selectionTimeRange);
      return contextRange;
    });

    // Update visible range only on initial mount or when explicitly needed
    // (not when selection changes - that should be controlled by the user via pan/zoom)
    const hasInitializedRef = useRef(false);
    React.useEffect(() => {
      if (!hasInitializedRef.current) {
        hasInitializedRef.current = true;
        const contextRange = model.buildContextRange(selectionTimeRange);
        setVisibleRange(contextRange);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Trigger batch loading when visible range changes
    React.useEffect(() => {
      model.updateVisibleRange(visibleRange.from, visibleRange.to);
    }, [model, visibleRange.from, visibleRange.to]);

    React.useEffect(() => {
      if (!containerRef.current) {
        return;
      }

      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          setWidth(entry.contentRect.width - 4); // 4px for border
        }
      });

      observer.observe(containerRef.current);

      return () => observer.disconnect();
    }, []);

    const onRangeChange = useCallback(
      (range: AbsoluteTimeRange) => {
        if (!sceneTimeRange) {
          return;
        }

        const nextRange: TimeRange = {
          from: dateTime(range.from),
          to: dateTime(range.to),
          raw: {
            from: dateTime(range.from),
            to: dateTime(range.to),
          },
        };

        sceneTimeRange.onTimeRangeChange(nextRange);
      },
      [sceneTimeRange]
    );

    // Callback when TimeSeeker's visible range changes (pan/zoom on context)
    const onVisibleRangeChange = useCallback((range: AbsoluteTimeRange) => {
      setVisibleRange(range);
    }, []);

    // Get cached data, loading state, and errors
    const { series, loading } = model.getCachedData();
    const loadingRanges = model.getLoadingRanges();
    const hasLargeBatchWarning = model.hasLargeBatchWarning();
    const errors = model.getErrors();
    const hasData = series.length > 0;
    const hasErrors = errors.length > 0;

    // Check for max duration error and provide a more helpful message
    const maxDurationError =
      'The selected time range exceeds the maximum allowed duration. Please select a shorter time range.';
    const maxDurationErrorMessage =
      'The time seeker requires the maximum range to be at least 24h, please update query_frontend.metrics.max_duration in your Tempo configuration file.';
    const isMaxDurationError = errors.some((e) => e === maxDurationError);
    const displayError = isMaxDurationError ? maxDurationErrorMessage : errors[0];

    // Build panel data from cached series
    const seekerData = React.useMemo(() => {
      if (!hasData) {
        return null;
      }

      const timeRange = selectionTimeRange ?? {
        from: dateTime(visibleRange.from),
        to: dateTime(visibleRange.to),
        raw: {
          from: dateTime(visibleRange.from),
          to: dateTime(visibleRange.to),
        },
      };

      return {
        state: loading ? LoadingState.Loading : LoadingState.Done,
        series,
        timeRange,
        structureRev: 1,
      };
    }, [hasData, series, loading, visibleRange, selectionTimeRange]);

    const errorDetail = displayError ?? (errors.length > 0 ? errors.join('; ') : 'Unknown error');

    React.useEffect(() => {
      if (!hasErrors) {
        return;
      }
      if (!hasData) {
        console.error('Failed to load time seeker data', errorDetail);
      } else {
        console.warn('Some time seeker data failed to load', errorDetail);
      }
    }, [hasErrors, hasData, errorDetail]);

    // Hide the seeker row entirely on load failure; details are logged to the console (see effect above).
    if (hasErrors && !hasData) {
      return null;
    }

    const wrapSeekerRow = (body: React.ReactNode) => (
      <div className={styles.seekerRow}>
        <div className={styles.seekerLabel}>Seeker</div>
        <div className={styles.container} ref={containerRef}>
          {body}
        </div>
      </div>
    );

    // Show loading state if no data yet
    if (!seekerData) {
      return wrapSeekerRow(
        <div className={styles.placeholder}>
          <Spinner size={16} />
          <Text variant="bodySmall" color="secondary">
            Loading time seeker…
          </Text>
        </div>
      );
    }

    return wrapSeekerRow(
      width > 0 ? (
        <TimeSeeker
          data={seekerData}
          width={width}
          metric={metricValue as MetricFunction}
          initialVisibleRange={visibleRange}
          onChangeTimeRange={onRangeChange}
          onVisibleRangeChange={onVisibleRangeChange}
          loadingRanges={loadingRanges}
          hasLargeBatchWarning={hasLargeBatchWarning}
        />
      ) : null
    );
  };

  // This will be set by the component to trigger re-renders
  public forceRender: () => void = () => {};
}

const getStyles = (theme: GrafanaTheme2) => ({
  seekerRow: css({
    display: 'flex',
    flexDirection: 'row',
    marginLeft: '35px',
    marginTop: '-8px',
  }),
  seekerLabel: css({
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1),
    fontSize: theme.typography.bodySmall.fontSize,
    color: theme.colors.text.secondary,
  }),
  container: css({
    flex: 1,
    minWidth: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  }),
  placeholder: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    height: '100%',
    padding: theme.spacing(1),
    color: theme.colors.text.secondary,
  }),
});
